import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/*
 * The button system.
 *
 * ---- Hierarchy ---------------------------------------------------------------
 *
 * Five tiers, and a screen should read them at a glance without reading the labels:
 *
 *   default      the filled green. One per view — the thing the page is for.
 *   secondary    bordered card surface. Cancel, Back, Export, View.
 *   ghost        no chrome until hovered. Toolbar and inline utilities.
 *   destructive  a subtle warm surface, not a red fill. Delete, Revoke, Reset.
 *   subtle       the accent tint, for a tonal action beside a filled primary.
 *
 * `link` is the sixth and is not really in the hierarchy — it is a link that needs
 * button semantics, and it carries no control geometry at all.
 *
 * ---- Geometry ----------------------------------------------------------------
 *
 * Every size is declared mobile-first at the larger of its two heights and steps
 * down at `sm:`. That is the right way round: 44px is the touch floor and 40px is
 * comfortable with a mouse, so the phone gets the generous tier and the desktop
 * gets the dense one. The tiers below 36px never reach the touch floor by height,
 * so they carry `.touch-target`, which pads the hit area to 44×44 on coarse
 * pointers without changing a pixel of what is painted.
 *
 * ---- Motion ------------------------------------------------------------------
 *
 * One curve for the whole system (`--ease-control`, the standard curve) and one
 * duration per state: 180ms in, 120ms under the press, 150ms for focus. Only
 * transform, opacity, box-shadow and the three colour properties are animated —
 * nothing that costs layout, which is what keeps a hover on a table full of
 * buttons free.
 */

const buttonVariants = cva(
  [
    'relative inline-flex select-none items-center justify-center whitespace-nowrap align-middle',
    'font-semibold tracking-[0.01em]',
    'transition-[color,background-color,border-color,box-shadow,transform,opacity]',
    'duration-hover ease-control active:duration-press',
    // The product's focus indicator: an outline, so the 2px offset is genuinely
    // transparent and one treatment is correct on every surface. See styles/index.css.
    'focus-ring',
    // No `pointer-events-none` on the disabled state: the cursor is how a pointer
    // user learns the control is refusing them, and removing pointer events removes
    // the cursor with it. `:disabled` on a real button already blocks activation,
    // and the disabled variants win over the hover ones because Tailwind orders
    // `disabled:` last.
    'disabled:cursor-not-allowed',
    '[&_svg]:pointer-events-none [&_svg]:shrink-0',
    '[&_svg]:transition-transform [&_svg]:duration-hover [&_svg]:ease-control',
  ],
  {
    variants: {
      variant: {
        /*
         * Filled indigo with white text. At 7.9:1 that pairing is unambiguous, and
         * the accent is light enough that a tinted foreground would not clear AA.
         *
         * The only tier that lifts. 1px of travel and a wide tinted shadow is the
         * whole gesture: enough to read as the page's one raised object, small
         * enough that a row of them does not shimmer as the pointer crosses it.
         */
        default: [
          'bg-primary text-primary-foreground shadow-xs',
          'hover:-translate-y-px hover:bg-primary-hover hover:shadow-lift',
          // The press cancels the lift and takes the shadow with it, so the control
          // reads as travelling back into the page rather than merely shrinking.
          'active:translate-y-0 active:scale-[0.98] active:shadow-lift-sm',
          'disabled:translate-y-0 disabled:bg-control-disabled disabled:text-control-disabled-foreground disabled:shadow-none',
        ],
        /*
         * The bordered tier. Hover lightens the surface and darkens the border by one
         * step each — the label never changes, because a secondary action that
         * brightens its text on hover starts competing with the primary beside it.
         */
        secondary: [
          'border border-border bg-card text-foreground shadow-xs',
          'hover:border-shape hover:bg-card-hover hover:shadow-sm',
          'active:scale-[0.99] active:shadow-xs',
          'focus-visible:border-primary',
          'disabled:border-border disabled:bg-muted disabled:text-control-disabled-foreground disabled:shadow-none',
        ],
        /*
         * No chrome at rest. The wash is the action colour at 8% rather than a grey:
         * a neutral hover on a warm page reads as a smudge, where a tint of the green
         * reads as the same material the primary is made of.
         */
        ghost: [
          'text-control-ghost',
          'hover:bg-control-wash',
          'active:scale-[0.99] active:bg-control-wash-strong',
          'disabled:bg-transparent disabled:text-control-disabled-foreground',
        ],
        /*
         * Deliberately quiet. A saturated red button is the loudest object on any
         * screen it appears on, which inverts the hierarchy — deleting is rare and
         * belongs below the page's actual purpose. The warning is carried by the
         * label, at 7.1:1 on its own surface, not by the fill.
         *
         * The focus halo is re-pointed at the destructive tone by overriding --ring
         * locally, so confirming a delete from the keyboard is not haloed in the
         * same green as saving a form.
         */
        destructive: [
          'border border-destructive-border bg-destructive-surface text-destructive-strong shadow-xs',
          '[--ring:var(--destructive-strong)]',
          'hover:border-destructive-border-hover hover:bg-destructive-surface-hover hover:shadow-sm',
          'active:scale-[0.99] active:shadow-xs',
          'focus-visible:border-destructive-strong',
          'disabled:border-border disabled:bg-muted disabled:text-control-disabled-foreground disabled:shadow-none',
        ],
        /* Tonal: the accent tint, for a secondary action sitting next to a filled
           primary. An opacity-tinted indigo would go milky on the off-white page, so
           this uses the palette's own tint step instead. */
        subtle: [
          'bg-accent text-accent-foreground',
          'hover:bg-shape/70',
          'active:scale-[0.99]',
          'disabled:bg-muted disabled:text-control-disabled-foreground',
        ],
        link: [
          'text-control-ghost underline-offset-4',
          'hover:underline',
          'disabled:text-control-disabled-foreground disabled:no-underline',
        ],
      },
      /*
       * `default` is the 44px page-and-form tier the palette specifies its primary
       * at. `compact` is the 40px toolbar tier and is what ghost buttons resolve to.
       * The dense tiers below carry table-row and in-field actions, where scaling to
       * 44px would push a row of controls taller than the row they act on.
       */
      size: {
        default: 'h-12 rounded-control px-5 text-base [--btn-gap:0.5rem] sm:h-11 [&_svg]:size-4',
        compact:
          'h-11 rounded-control-sm px-4 text-base [--btn-gap:0.5rem] sm:h-10 [&_svg]:size-4',
        lg: 'h-13 rounded-control px-7 text-md [--btn-gap:0.5rem] sm:h-12 [&_svg]:size-[1.125rem]',
        sm: 'h-11 rounded-control-sm px-3.5 text-sm [--btn-gap:0.5rem] sm:h-9 [&_svg]:size-4',
        xs: 'touch-target h-7 rounded-control-sm px-2.5 text-xs [--btn-gap:0.375rem] [&_svg]:size-3.5',
        /* 40×40 on a mouse, 44×44 under a thumb. */
        icon: 'size-11 rounded-control [--btn-gap:0.5rem] sm:size-10 [&_svg]:size-4',
        'icon-sm': 'touch-target size-9 rounded-control-sm [--btn-gap:0.5rem] sm:size-8 [&_svg]:size-4',
        'icon-xs': 'touch-target size-7 rounded-control-sm [--btn-gap:0.375rem] sm:size-6 [&_svg]:size-3.5',
        /* No box at all — for `link`, which should sit on the text baseline it was
           written into rather than reserve a control's worth of height. */
        inline: 'h-auto rounded-control-sm px-0 text-base [--btn-gap:0.375rem] [&_svg]:size-4',
      },
      /*
       * A 1px flourish on the icon, chosen per button rather than applied to all of
       * them: an arrow should travel in the direction it points, a refresh or a
       * settings glyph should turn, and a plus should do neither. Applied to the
       * icons only, never to the label, so the text stays perfectly still.
       *
       * The loading spinner is excluded by selector — it is already animating, and
       * nudging it sideways under the pointer reads as a glitch.
       */
      iconMotion: {
        none: '',
        shift: 'hover:[&_svg:not([data-spinner])]:translate-x-px',
        back: 'hover:[&_svg:not([data-spinner])]:-translate-x-px',
        lift: 'hover:[&_svg:not([data-spinner])]:-translate-y-px',
        rotate: 'hover:[&_svg:not([data-spinner])]:-rotate-[18deg]',
      },
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: { iconMotion: 'none', fullWidth: false },
  },
);

/* Sizes whose footprint is a square glyph rather than a label. */
const ICON_SIZES = new Set(['icon', 'icon-sm', 'icon-xs']);

/*
 * What a variant paints while it is loading.
 *
 * A busy button is `disabled` — that is the only reliable way to stop a second
 * submit reaching the handler — but it must not *look* disabled. Switched-off and
 * working-on-it are opposite messages, and the request the reader just started is
 * the one thing they most want confirmation of.
 *
 * These are `disabled:` utilities on purpose. Appended after the variant's own
 * disabled classes, tailwind-merge drops the earlier value from each conflicting
 * group, so the resting skin wins without depending on stylesheet order.
 */
const LOADING_SKIN: Record<string, string> = {
  default: 'disabled:bg-primary disabled:text-primary-foreground disabled:shadow-xs',
  secondary: 'disabled:border-border disabled:bg-card disabled:text-foreground disabled:shadow-xs',
  ghost: 'disabled:bg-transparent disabled:text-control-ghost',
  destructive:
    'disabled:border-destructive-border disabled:bg-destructive-surface disabled:text-destructive-strong disabled:shadow-xs',
  subtle: 'disabled:bg-accent disabled:text-accent-foreground',
  link: 'disabled:text-control-ghost',
};

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render the child element instead of a `button`, keeping the styling. */
  asChild?: boolean;
  /**
   * Show a spinner and block interaction while an action is in flight.
   *
   * Ignored when `asChild` is set, because Slot requires exactly one child and
   * cannot accommodate an injected spinner.
   */
  loading?: boolean;
  /**
   * The label to show in place of the children while `loading`.
   *
   * Prefer this over swapping the children yourself: the resting label stays
   * mounted to hold the button's width, so the control cannot resize as the
   * request starts. Keep it shorter than the resting label — "Saving…" under
   * "Save changes" — since anything longer is the one thing that can still make
   * the button grow.
   */
  loadingText?: string;
}

/**
 * The product's only button.
 *
 * Two defaults are inferred rather than required, because the alternative is every
 * call site repeating the same pairing:
 *
 *   - a ghost or link button with no explicit size resolves to the compact tier,
 *     which is the height those tiers are specified at;
 *   - an icon-sized button with no explicit variant resolves to `secondary`, which
 *     is what the icon-button spec describes — a bordered card surface with the
 *     action colour inside it. Passing `variant="ghost"` still gets a ghost.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      iconMotion,
      fullWidth,
      asChild = false,
      loading = false,
      loadingText,
      disabled,
      children,
      ...props
    },
    ref,
  ) => {
    // Each default reads the *raw* other prop, so the two inferences cannot
    // feed each other.
    const resolvedVariant = variant ?? (size && ICON_SIZES.has(size) ? 'secondary' : 'default');
    const resolvedSize =
      size ?? (variant === 'link' ? 'inline' : variant === 'ghost' ? 'compact' : 'default');

    // `asChild` cannot take a spinner: Slot requires exactly one child.
    const isBusy = loading && !asChild;

    const classes = cn(
      buttonVariants({
        variant: resolvedVariant,
        size: resolvedSize,
        iconMotion,
        fullWidth,
      }),
      isBusy ? LOADING_SKIN[resolvedVariant] : null,
      // Working-on-it rather than refused, so the cursor says so too.
      isBusy ? 'disabled:cursor-progress' : null,
      className,
    );

    if (asChild) {
      return (
        <Slot className={classes} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={classes}
        ref={ref}
        disabled={isBusy || disabled}
        aria-busy={isBusy || undefined}
        {...props}
      >
        {/*
          The resting content stays mounted and merely goes invisible, which is what
          fixes the button's width for the duration of the request. Swapping it out
          for the spinner would let a "Save changes" button collapse to the width of
          a 16px glyph the instant it is pressed.
        */}
        <span
          className={cn(
            'inline-flex items-center justify-center gap-[var(--btn-gap)] whitespace-nowrap',
            isBusy ? 'invisible' : null,
          )}
        >
          {children}
        </span>

        {isBusy ? (
          <span className="absolute inset-0 inline-flex items-center justify-center gap-[var(--btn-gap)] whitespace-nowrap">
            <Loader2 className="animate-spin" data-spinner aria-hidden="true" />
            {loadingText ? <span className="opacity-75">{loadingText}</span> : null}
          </span>
        ) : null}
      </button>
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
